import { TASK_EVENT_ACTIONS } from '@nx-frontend/features/web-editor/types';
import { WindowCommon, CustomSubscription } from '@nx-frontend/features/web-editor/types';
import { onWindowCommonReady } from '@nx-frontend/features/web-editor/util';
import { ToastProps } from '@nx-frontend/shared/ui/toast';
import { InvitePopupPayload, PopupType, TranslatesFnType } from '@nx-frontend/shared/types';
import { copyToClipboard } from '@nx-frontend/shared/utils';

export class TaskEventsService {
  public subscription: CustomSubscription | null = null;

  constructor(private toast: (params: ToastProps) => void, private t?: TranslatesFnType) {
    onWindowCommonReady((windowCommon: WindowCommon) => {
      this.listenEvents(windowCommon);
    });
  }

  static inited = false;
  static openInvitePopup: (payload: InvitePopupPayload) => void;
  static navigateToDashboard: () => void;
  static goToPage: (workspaceId: string, noteId: string) => void | undefined;

  static init(toast: (params: ToastProps) => void, t?: TranslatesFnType) {
    if (!TaskEventsService.inited) {
      new TaskEventsService(toast, t);
      TaskEventsService.inited = true;
    }
  }

  get gettextCatalog() {
    return {
      getString: (text: string) => text,
    };
  }

  get Navigator() {
    return {
      goToNote: (noteId: string) => console.log('goToNote', noteId),
      goToNoteInOtherWorkspace: (noteId: string, workspaceId: string) => {
        if (TaskEventsService.goToPage) {
          TaskEventsService.goToPage(workspaceId, noteId);
        }
      },
      getTaskLink: (workspaceId: string, taskId: string) => {
        return `${window.location.origin}/space/${workspaceId}/tasks/${taskId}`;
      },
      goToTasksDashboard: () => {
        if (TaskEventsService.navigateToDashboard) {
          TaskEventsService.navigateToDashboard();
        }
      },
    };
  }

  listenEvents(windowCommon: WindowCommon) {
    this.subscription = windowCommon.taskEventsSubj$.subscribe((event) => {
      switch (event.eventType) {
        case 'note:open':
          if (typeof event.value === 'string') {
            return this.Navigator.goToNote(event.value);
          }
          break;
        case TASK_EVENT_ACTIONS.DATE_LIMIT:
          return this.showTasksLimitPopup('tasks_premium_date');
        case TASK_EVENT_ACTIONS.REMINDER_LIMIT:
          return this.showTasksLimitPopup('tasks_premium_alerts');
        case TASK_EVENT_ACTIONS.TASK_LIST_COMPLETE_ALL:
          return this.showToast(this.gettextCatalog.getString('Done'));
        case TASK_EVENT_ACTIONS.TASK_LIST_UNCOMPLETE_ALL:
          return this.showToast(this.gettextCatalog.getString('Done'));
        case TASK_EVENT_ACTIONS.INVITE_MEMBERS:
          return this.openInvitePopup();
        case TASK_EVENT_ACTIONS.ASSIGNEE_LIMIT:
          // return this.showToast(this.gettextCatalog.getString('You can assign up to three members to a task.'));
          return this.showToast(
            this.gettextCatalog.getString(
              (this.t && this.t('features.tasks.assign_up_to_three_members')) ??
                'You can assign up to three members to a task.'
            )
          );
        case TASK_EVENT_ACTIONS.TASK_COPY_LINK:
          if (typeof event.value === 'object') {
            return this.copyTaskLink(event.value.workspaceId, event.value.taskId);
          }
          break;
        case TASK_EVENT_ACTIONS.TASK_DUPLICATED:
          return this.showToast(
            this.gettextCatalog.getString((this.t && this.t('features.tasks.task_duplicated')) ?? 'Task duplicated')
          );
        case TASK_EVENT_ACTIONS.DESCRIPTION_PREMIUM_LIMIT:
          return this.showTasksLimitErrorPopup('description');
        case TASK_EVENT_ACTIONS.COMMENTS_PREMIUM_LIMIT:
          return this.showTasksLimitErrorPopup('comments');
        case TASK_EVENT_ACTIONS.ATTACHMENTS_PREMIUM_LIMIT:
          return this.showTasksLimitErrorPopup('attachments');
        case TASK_EVENT_ACTIONS.ESTIMATE_PREMIUM_LIMIT:
          return this.showTasksLimitErrorPopup('estimate');
        case TASK_EVENT_ACTIONS.TIME_PREMIUM_LIMIT:
          return this.showTasksLimitErrorPopup('time');
        case TASK_EVENT_ACTIONS.NOTE_NAVIGATE:
          if (typeof event.value === 'object') {
            return this.navigateToNote(event.value.workspaceId, event.value.noteId);
          }
          break;
        case TASK_EVENT_ACTIONS.TASK_NOT_FOUND:
          return this.showToast(
            this.gettextCatalog.getString(
              (this.t && this.t('features.tasks.task_not_found')) ?? 'Sorry, task not found.'
            )
          );
        case TASK_EVENT_ACTIONS.WORKSPACE_LABEL_UNIQUE_TITLE_ERROR:
          return this.showToast(
            this.gettextCatalog.getString(
              (this.t && this.t('features.tasks.label_already_exists')) ?? 'Label with this name already exists.'
            )
          );
        case TASK_EVENT_ACTIONS.TASK_DROP_DISABLED:
          return this.showToast(
            this.gettextCatalog.getString(
              (this.t && this.t('features.tasks.cannot_transfer_task_ws')) ??
                'You cannot transfer the task to the list from another workspace.'
            )
          );
        case TASK_EVENT_ACTIONS.DASHBOARD_NAVIGATE:
          return this.navigateToDashboard();
      }
    });
  }

  openInvitePopup = () => {
    TaskEventsService.openInvitePopup({
      payloadType: PopupType.INVITE,
      subjectType: 'workspace',
    });
  };

  showTasksLimitPopup = (reason: string) => {
    console.log('show tasks limit popup');
    // const state = this.$ngRedux.getState();
    // const orgId = getCurrentOrganizationId(state);
    // this.$ngRedux.dispatch(PopupsActions.closeFirstPopup());
    // this.$ngRedux.dispatch(
    //   this.AppActions.showPremiumInfoPopup({
    //     reason,
    //     pricingReason: 'premium_banner_limit_ws',
    //     title: this.gettextCatalog.getString('Remind your team about deadlines with an advanced plan.'),
    //     orgId: orgId,
    //   })
    // );
  };

  showToast = (title: string) => {
    this.toast({ title });
  };

  copyTaskLink = (workspaceId: string, taskId: string) => {
    const url = this.Navigator.getTaskLink(workspaceId, taskId);
    copyToClipboard(url);
    this.toast({ title: this.gettextCatalog.getString((this.t && this.t('webnotes.webnotes-copied')) ?? 'Copied') });
  };

  showTasksLimitErrorPopup = (error: 'description' | 'comments' | 'attachments' | 'estimate' | 'time') => {
    // const state = this.$ngRedux.getState();
    // const orgId = getCurrentOrganizationId(state);
    // const currentOrg = getCurrentOrganization(state);
    // const isOwner = currentOrg?.access.role === 'owner';
    // const isPremium = currentOrg?.info.subscriptionType === 'premium';
    // let reason = '';
    // let title = '';
    // if (error === 'description') {
    //   reason = 'task_description';
    //   title = this.gettextCatalog.getString(
    //     'Avoid hundreds of iterations. Add precise descriptions to your tasks with an advanced plan.'
    //   );
    //   if (!isOwner) {
    //     title =
    //       'Add task descriptions is included in the premium plan. Contact the organization owner for more details.';
    //   }
    // } else if (error === 'comments') {
    //   reason = 'task_comment';
    //   title = this.gettextCatalog.getString(
    //     'Shoot your questions for the task in the comments. Upgrade your plan to keep your discussions in one place.'
    //   );
    //   if (!isOwner) {
    //     title =
    //       'Add comments to task is included in the premium plan. Contact the organization owner for more details.';
    //   }
    // } else if (error === 'attachments') {
    //   reason = 'task_attachment';
    //   title = this.gettextCatalog.getString('Add files to tasks without restrictions');
    //   if (!isOwner) {
    //     title =
    //       'Add files to tasks without restrictions is included in the premium plan. Contact the organization owner for more details.';
    //   }
    // } else if (error === 'estimate') {
    //   reason = 'task_estimate';
    //   title = this.gettextCatalog.getString(
    //     'Upgrade to the Business Plan for unlimited access to Estimated time feature'
    //   );
    //   if (!isOwner) {
    //     title =
    //       'Estimate time feature is included in the business plan. Contact the organization owner for more details.';
    //   }
    // } else if (error === 'time') {
    //   reason = 'task_time';
    //   title = this.gettextCatalog.getString(
    //     'Upgrade to the Business Plan for unlimited access to Time Tracking feature'
    //   );
    //   if (!isOwner) {
    //     title =
    //       'Time Tracking feature is included in the business plan. Contact the organization owner for more details.';
    //   }
    // }
    // this.$ngRedux.dispatch(PopupsActions.closeFirstPopup());
    // if (isOwner) {
    //   let showPopupFunction = this.AppActions.showPremiumInfoPopup;
    //   // в случае если уже есть подписка pro, то в попапе нужно показываю её неактивной
    //   if (isPremium) {
    //     showPopupFunction = this.AppActions.OrganizationsActions.showUpgradeToBusinessPopup;
    //   }
    //   this.$ngRedux.dispatch(
    //     showPopupFunction({
    //       reason,
    //       pricingReason: 'premium_banner_limit_ws',
    //       title,
    //       orgId: orgId,
    //     })
    //   );
    // } else {
    //   const confirmPopup = {
    //     content: this.gettextCatalog.getString(title),
    //     okButtonTitle: this.gettextCatalog.getString('Ok'),
    //     hideCancelButton: true,
    //     onSuccess: () => {
    //       this.$ngRedux.dispatch(PopupsActions.closeFirstPopup());
    //     },
    //   };
    //   this.$ngRedux.dispatch(PopupsActions.showConfirmPopup(confirmPopup));
    // }
  };

  navigateToNote = (workspaceId: string, noteId: string) => {
    this.Navigator.goToNoteInOtherWorkspace(noteId, workspaceId);
  };

  navigateToDashboard = () => {
    this.Navigator.goToTasksDashboard();
  };

  stopListenEvents() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
